import { TaskSubTicketAssignmentResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { TaskSubTicketStatus } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources/TaskSubTicketResource';

// The pipeline as the work actually progresses. The aside's status counts already read in this
// order, so a designer's phases must too — a status-string sort would put `complete` first and
// the two blocks would disagree while describing the same phases.
export const STATUS_PIPELINE: TaskSubTicketStatus[] = ['to_design', 'in_progress', 'in_revision', 'complete'];

// Presentation order for the aside's per-assignment rows.
//
// Deliberately NOT a deduplication: one designer holding two phases in two different statuses is
// the case the aside exists to state, and collapsing them is the bug this replaced. Every input
// row comes out, exactly once.
//
// Designers keep the order the server sent them (first name, then last name) and the first row
// for a designer fixes their place, so moving one phase forward never reshuffles the list. Within
// a designer the order is pipeline status, then the immutable `#ordinal` label — a total order,
// so two reads of unchanged data render identically.
export const resolveAssigneeAssignments = (
    assignments: TaskSubTicketAssignmentResource[]
): TaskSubTicketAssignmentResource[] => {
    const designerOrder = new Map<ID, number>();
    assignments.forEach((assignment) => {
        if (!designerOrder.has(assignment.assignee_user.id)) {
            designerOrder.set(assignment.assignee_user.id, designerOrder.size);
        }
    });

    const placeOf = (assignment: TaskSubTicketAssignmentResource) =>
        designerOrder.get(assignment.assignee_user.id) ?? Number.MAX_SAFE_INTEGER;

    // Copied first: the caller's array is store-owned and must not be reordered under it
    const rows = [...assignments];

    rows.sort((a, b) => {
        const byDesigner = placeOf(a) - placeOf(b);
        if (byDesigner !== 0) {
            return byDesigner;
        }

        const byStatus = STATUS_PIPELINE.indexOf(a.status) - STATUS_PIPELINE.indexOf(b.status);
        if (byStatus !== 0) {
            return byStatus;
        }

        return a.ordinal - b.ordinal;
    });

    return rows;
};
